import type {
  ActivitiesFeed,
  ActivityEvent,
  ActivityJourneyBeat,
  ActivityMember,
} from "./activitiesTypes";
import { addDays, eventsForDay, localDayKey, summarizeDay } from "./groupActivitiesByDay";

const MOCK_ORG_ID = "org_mock_acme";

const sarah: ActivityMember = {
  userId: "user_mock_sarah",
  name: "Sarah Chen",
  title: "Head of Operations",
  role: "Leadership",
  avatarSeed: "iris",
  isAdmin: true,
};

const james: ActivityMember = {
  userId: "user_mock_james",
  name: "James Reed",
  title: "Account Executive",
  role: "Sales",
  avatarSeed: "canyon",
};

const maya: ActivityMember = {
  userId: "user_mock_maya",
  name: "Maya Patel",
  title: "Marketing Manager",
  role: "Marketing",
  avatarSeed: "jade",
};

function atDay(base: Date, dayOffset: number, hours: number, minutes = 0): string {
  const d = addDays(base, dayOffset);
  d.setHours(hours, minutes, 0, 0);
  return d.toISOString();
}

function journey(
  anchor: Date,
  dayOffset: number,
  beats: readonly { h: number; m?: number; detail: string }[],
): ActivityJourneyBeat[] {
  return beats.map((b) => ({
    detail: b.detail,
    at: atDay(anchor, dayOffset, b.h, b.m ?? 0),
  }));
}

function buildEvents(anchor: Date): ActivityEvent[] {
  return [
    {
      id: "act_mock_01",
      orgId: MOCK_ORG_ID,
      actor: james,
      kind: "allowance",
      status: "needs_approval",
      title: "WhatsApp — send quote copy",
      summary:
        "Morrow drafted a Q3 pricing message for Ahmed Y.; outbound WhatsApp is blocked until an admin approves.",
      refs: { allowanceId: "allow_mock_01", threadId: "thread_mock_wa_1", agentLabel: "Morrow" },
      occurredAt: atDay(anchor, 0, 8, 45),
      dueAt: atDay(anchor, 0, 8, 45),
      journey: journey(anchor, 0, [
        {
          h: 8,
          m: 38,
          detail:
            "Main agent read CRM note “Ahmed Y. — warm lead” and composed a WhatsApp template with Q3 line items from vault:teklif-q3.pdf.",
        },
        {
          h: 8,
          m: 44,
          detail:
            "Policy engine routed to human review: first outbound quote to this contact requires Ops approval (org rule: sales_whatsapp_quote).",
        },
        {
          h: 8,
          m: 45,
          detail:
            "Pending decision — approve to send via WhatsApp Business API, edit in thread, or deny and keep the draft in vault only.",
        },
      ]),
    },
    {
      id: "act_mock_02",
      orgId: MOCK_ORG_ID,
      actor: maya,
      kind: "schedule",
      status: "pending",
      title: "Inbox digest",
      summary:
        "Every day at 9:00 AM — short summary of yesterday’s email threads assigned to Marketing.",
      refs: { workflowId: "wf_mock_inbox", workflowName: "Inbox digest" },
      occurredAt: atDay(anchor, 0, 0, 0),
      dueAt: atDay(anchor, 0, 9, 0),
      journey: journey(anchor, 0, [
        {
          h: 0,
          m: 0,
          detail:
            "Cron registered on workflow wf_mock_inbox; next run bound to Maya’s Gmail label “Marketing/Action”.",
        },
        {
          h: 9,
          m: 0,
          detail:
            "Due — scheduler will spawn a read-only agent run to summarize threads since last digest.",
        },
        {
          h: 9,
          m: 0,
          detail:
            "Not yet executed today; output will post to #marketing Slack and link threads in Activities.",
        },
      ]),
    },
    {
      id: "act_mock_03",
      orgId: MOCK_ORG_ID,
      actor: sarah,
      kind: "tool_action",
      status: "running",
      title: "Sending email",
      summary: "Subject: Q3 proposal — attachment: proposal-q3.pdf (Gmail send in flight).",
      refs: {
        threadId: "thread_mock_email_1",
        agentId: "main",
        agentLabel: "Morrow",
        toolName: "GMAIL_SEND_EMAIL",
        toolkit: "gmail",
        vaultItemId: "vault_mock_proposal",
        fileLabel: "proposal-q3.pdf",
      },
      occurredAt: atDay(anchor, 0, 10, 12),
      dueAt: null,
      journey: journey(anchor, 0, [
        {
          h: 10,
          m: 10,
          detail:
            "After allowance act_mock_08 cleared, send job queued on thread thread_mock_email_1 with signed vault attachment.",
        },
        {
          h: 10,
          m: 12,
          detail:
            "Gmail adapter executing GMAIL_SEND_EMAIL — waiting on provider ACK (typical 2–8s).",
        },
        {
          h: 10,
          m: 12,
          detail: "On success, thread will checkpoint and Activities will mark this step finished.",
        },
      ]),
    },
    {
      id: "act_mock_04",
      orgId: MOCK_ORG_ID,
      actor: james,
      kind: "workflow_run",
      status: "running",
      title: "Lead auto-reply",
      summary:
        "WhatsApp trigger — matching inbound keyword “pricing” with a templated first response.",
      refs: {
        workflowId: "wf_mock_lead",
        workflowName: "Lead auto-reply",
        threadId: "thread_mock_lead",
        agentLabel: "Morrow",
      },
      occurredAt: atDay(anchor, 0, 10, 5),
      dueAt: null,
      journey: journey(anchor, 0, [
        {
          h: 10,
          m: 4,
          detail:
            "Inbound webhook from WhatsApp matched workflow wf_mock_lead (James’s sales line).",
        },
        {
          h: 10,
          m: 5,
          detail:
            "Sub-agent drafting reply; CRM lookup found existing lead — skipping duplicate create, attaching deal stage.",
        },
        {
          h: 10,
          m: 5,
          detail:
            "Will either send within 24h window or escalate to manual follow-up if template fails policy.",
        },
      ]),
    },
    {
      id: "act_mock_05",
      orgId: MOCK_ORG_ID,
      actor: sarah,
      kind: "agent_run",
      status: "completed",
      title: "HR — candidate sweep",
      summary:
        "LinkedIn: 12 profiles reviewed against the Senior PM rubric; 3 candidates added to the shortlist board.",
      refs: {
        agentId: "agent_hr_deniz",
        agentLabel: "Deniz (HR)",
        threadId: "thread_mock_hr",
        toolkit: "linkedin",
        toolName: "LINKEDIN_SEARCH",
      },
      occurredAt: atDay(anchor, 0, 9, 40),
      dueAt: null,
      journey: journey(anchor, 0, [
        {
          h: 9,
          m: 18,
          detail:
            "Spawned from board handoff act_mock_12 — CEO thread requested “Istanbul Senior PM shortlist by EOD”.",
        },
        {
          h: 9,
          m: 28,
          detail:
            "Deniz ran LINKEDIN_SEARCH + profile scrape; scored each profile against vault:hr-rubric-senior-pm.md.",
        },
        {
          h: 9,
          m: 40,
          detail:
            "Posted summary to #hiring, wrote 3 cards to HR board, and linked source URLs in thread thread_mock_hr.",
        },
      ]),
    },
    {
      id: "act_mock_06",
      orgId: MOCK_ORG_ID,
      actor: maya,
      kind: "workflow_run",
      status: "completed",
      title: "Invoice reminder",
      summary:
        "Reminder emails sent to 3 accounts with invoices 7+ days overdue (Finance CC on each).",
      refs: {
        workflowId: "wf_mock_invoice",
        workflowName: "Invoice reminder",
        agentLabel: "Morrow",
      },
      occurredAt: atDay(anchor, 0, 9, 15),
      dueAt: null,
      journey: journey(anchor, 0, [
        {
          h: 9,
          m: 0,
          detail:
            "Scheduled workflow wf_mock_invoice pulled overdue rows from billing export in vault.",
        },
        {
          h: 9,
          m: 8,
          detail:
            "Agent personalized three templates; each passed low-risk send policy without allowance.",
        },
        {
          h: 9,
          m: 15,
          detail:
            "Gmail confirmed delivery for all three; Finance audit log updated in thread thread_mock_billing.",
        },
      ]),
    },
    {
      id: "act_mock_07",
      orgId: MOCK_ORG_ID,
      actor: sarah,
      kind: "tool_action",
      status: "completed",
      title: "File uploaded",
      summary:
        "Google Drive — Marketing/Q3-campaign-brief.docx (shared with Maya, link-back to campaign thread).",
      refs: {
        agentId: "subagent_mock_1",
        agentLabel: "Morb",
        toolkit: "googledrive",
        toolName: "GOOGLEDRIVE_UPLOAD",
        fileLabel: "Q3-campaign-brief.docx",
        threadId: "thread_mock_drive",
      },
      occurredAt: atDay(anchor, 0, 8, 20),
      dueAt: null,
      journey: journey(anchor, 0, [
        {
          h: 8,
          m: 15,
          detail:
            "Morb sub-agent received upload intent from Sarah’s chat: “put the brief in Marketing/Q3”.",
        },
        {
          h: 8,
          m: 18,
          detail:
            "GOOGLEDRIVE_UPLOAD ran with org service account; set view-only for external guests off.",
        },
        {
          h: 8,
          m: 20,
          detail: "Drive link pinned to thread_mock_drive and indexed for Activities search.",
        },
      ]),
    },
    {
      id: "act_mock_08",
      orgId: MOCK_ORG_ID,
      actor: sarah,
      kind: "allowance",
      status: "approved",
      title: "Email sent — vendor notice",
      summary: "Approved — supplier status update delivered to procurement@partner.co (09:12).",
      refs: {
        allowanceId: "allow_mock_done",
        threadId: "thread_mock_email_0",
        toolkit: "gmail",
        toolName: "GMAIL_SEND_EMAIL",
        agentLabel: "Morrow",
      },
      occurredAt: atDay(anchor, 0, 9, 12),
      dueAt: null,
      journey: journey(anchor, 0, [
        {
          h: 9,
          m: 5,
          detail:
            "Agent drafted vendor notice from Sarah’s bullet list; classified as external send (allowance required).",
        },
        {
          h: 9,
          m: 8,
          detail: "Sarah approved in Allowance UI — comment: “LGTM, send as-is.”",
        },
        {
          h: 9,
          m: 12,
          detail:
            "Gmail send completed; message-id stored on thread_mock_email_0 and marked approved in ledger.",
        },
      ]),
    },
    {
      id: "act_mock_09",
      orgId: MOCK_ORG_ID,
      actor: james,
      kind: "comms",
      status: "failed",
      title: "WhatsApp reply failed",
      summary:
        "Outside the 24-hour session window — customer must message first; manual follow-up required.",
      refs: { threadId: "thread_mock_wa_fail", agentLabel: "Morrow" },
      occurredAt: atDay(anchor, -1, 16, 30),
      dueAt: null,
      journey: journey(anchor, -1, [
        {
          h: 16,
          m: 28,
          detail: "Inbound note from James: “nudge Ahmed again on WhatsApp.”",
        },
        {
          h: 16,
          m: 29,
          detail:
            "WhatsApp adapter rejected send — last customer message older than 24h (error: session_expired).",
        },
        {
          h: 16,
          m: 30,
          detail:
            "Run failed; agent suggested SMS or email fallback and opened a todo for James in Sales lane.",
        },
      ]),
    },
    {
      id: "act_mock_10",
      orgId: MOCK_ORG_ID,
      actor: maya,
      kind: "workflow_run",
      status: "completed",
      title: "Social weekly digest",
      summary:
        "Engagement report saved to vault and summarized in #marketing (PDF + bullet highlights).",
      refs: {
        workflowId: "wf_mock_social",
        workflowName: "Social weekly digest",
        vaultItemId: "vault_mock_report",
        fileLabel: "engagement-report.pdf",
        agentLabel: "Morrow",
      },
      occurredAt: atDay(anchor, -1, 11, 0),
      dueAt: null,
      journey: journey(anchor, -1, [
        {
          h: 10,
          m: 45,
          detail:
            "Monday cron triggered wf_mock_social; pulled metrics from connected social toolkits.",
        },
        {
          h: 10,
          m: 55,
          detail: "Agent compared week-over-week deltas and flagged one underperforming campaign.",
        },
        {
          h: 11,
          m: 0,
          detail:
            "PDF written to vault_mock_report; Slack digest posted with link and three action items.",
        },
      ]),
    },
    {
      id: "act_mock_11",
      orgId: MOCK_ORG_ID,
      actor: james,
      kind: "agent_run",
      status: "completed",
      title: "CRM stage updates",
      summary: "HubSpot: 5 open deals moved to the correct pipeline stages from last call notes.",
      refs: { agentId: "agent_sales_1", agentLabel: "Sales Bot", toolkit: "hubspot" },
      occurredAt: atDay(anchor, -1, 14, 20),
      dueAt: null,
      journey: journey(anchor, -1, [
        {
          h: 14,
          m: 10,
          detail: "James @mentioned Sales Bot in deal-review thread with pasted call summaries.",
        },
        {
          h: 14,
          m: 15,
          detail:
            "Bot parsed five accounts, matched HubSpot IDs, proposed stage transitions with confidence scores.",
        },
        {
          h: 14,
          m: 20,
          detail:
            "All five updates committed; activity receipt linked back to James’s thread for audit.",
        },
      ]),
    },
    {
      id: "act_mock_12",
      orgId: MOCK_ORG_ID,
      actor: sarah,
      kind: "board",
      status: "completed",
      title: "Agent handoff",
      summary: "CEO → HR: request a shortlist for the new Senior PM role (Istanbul, hybrid).",
      refs: { agentId: "agent_ceo", agentLabel: "CEO" },
      occurredAt: atDay(anchor, -1, 10, 5),
      dueAt: null,
      journey: journey(anchor, -1, [
        {
          h: 10,
          m: 2,
          detail:
            "CEO agent completed board standup summary and opened a handoff card on the Leadership board.",
        },
        {
          h: 10,
          m: 4,
          detail:
            "Routing assigned Deniz (HR) as owner with deadline today; copied Sarah as approver.",
        },
        {
          h: 10,
          m: 5,
          detail: "Handoff accepted — downstream agent_run act_mock_05 started automatically.",
        },
      ]),
    },
    {
      id: "act_mock_13",
      orgId: MOCK_ORG_ID,
      actor: maya,
      kind: "schedule",
      status: "pending",
      title: "Weekly competitor scan",
      summary:
        "Monday 8:30 AM — routine workflow comparing competitor landing pages and ad libraries.",
      refs: { workflowId: "wf_mock_compete", workflowName: "Competitor scan" },
      occurredAt: atDay(anchor, 1, 0, 0),
      dueAt: atDay(anchor, 1, 8, 30),
      journey: journey(anchor, 1, [
        {
          h: 0,
          m: 0,
          detail:
            "Next occurrence scheduled; inputs: competitor URL list in vault:competitors-q3.json.",
        },
        {
          h: 8,
          m: 30,
          detail: "Due — will run headless browser snapshots and diff against last week’s archive.",
        },
        {
          h: 8,
          m: 30,
          detail:
            "Not run yet; Maya will get a Slack summary with changed headlines and new offers.",
        },
      ]),
    },
    {
      id: "act_mock_14",
      orgId: MOCK_ORG_ID,
      actor: james,
      kind: "schedule",
      status: "pending",
      title: "Pipeline summary",
      summary:
        "Tuesday 9:00 AM — sales pipeline roll-up for leadership (weighted forecast + stale deals).",
      refs: { workflowId: "wf_mock_pipeline", workflowName: "Pipeline summary" },
      occurredAt: atDay(anchor, 2, 0, 0),
      dueAt: atDay(anchor, 2, 9, 0),
      journey: journey(anchor, 2, [
        {
          h: 0,
          m: 0,
          detail:
            "Workflow wf_mock_pipeline armed for James’s HubSpot view “AE — open this quarter”.",
        },
        {
          h: 9,
          m: 0,
          detail: "Due — agent will compile CSV + narrative and post to #sales-leadership.",
        },
        {
          h: 9,
          m: 0,
          detail: "Awaiting schedule fire; no partial output yet.",
        },
      ]),
    },
  ];
}

/** Sample org feed for design review — dates anchored to “today” in the viewer’s TZ. */
export function mockActivitiesFeed(anchor = new Date()): ActivitiesFeed {
  const events = buildEvents(anchor);
  const summaryByDay: Record<string, ReturnType<typeof summarizeDay>> = {};
  for (let offset = -3; offset <= 3; offset += 1) {
    const key = localDayKey(addDays(anchor, offset));
    summaryByDay[key] = summarizeDay(eventsForDay(events, key));
  }

  return {
    members: [sarah, james, maya],
    events,
    summaryByDay,
  };
}

export const MOCK_ACTIVITIES_FEED = mockActivitiesFeed();
